import React, { FC, memo, ReactNode, useMemo } from 'react';

import './ParagraphList.css';

import { ColorlessIconProps, IconCloseFilled, IconSelectFilled } from '../../icons';
import { cn, getClassNames } from '../../lib/css/className/className';
import { useTypographyWithSkin } from '../../hooks/useTypography/useTypographyWithSkin';
import { useInternalVerticalAlignWithSkin } from '../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';

import { ParagraphListProps, ParagraphListSize, YesNoParagraphListNode } from './types';
import { isInnerParagraphListItem, paragraphListHeaderLevels } from './utils';

import './ParagraphList.css';

const cls = cn('ParagraphList');

type InternalParagraphListYesNoItemProps = YesNoParagraphListNode & { className?: string; size: ParagraphListSize };
const InternalParagraphListYesNoItem = function InternalParagraphListItem(props: InternalParagraphListYesNoItemProps) {
    const { isYes, content, ['data-testid']: dataTestid, isDisabled, size, className } = props;
    const itemDataTestId = dataTestid;

    const defaultProps: ColorlessIconProps = {
        // Размер не из скина DIRECT-238538
        size: '16',
        role: isDisabled ? 'supplementary' : 'main',
        className: cls('yes-no-bullet'),
    };

    const bullet = isYes ? (
        <IconSelectFilled {...defaultProps} color={isDisabled ? 'gray-disabled' : 'green'} />
    ) : (
        <IconCloseFilled {...defaultProps} color={isDisabled ? 'gray-disabled' : 'red'} />
    );

    return (
        <li className={cls('list-item')} data-testid={itemDataTestId} data-value={String(isYes)}>
            <div className={cls('bullet', [className])}>{bullet}</div>
            <TextGenericWithSkin
                textSize="current-main"
                skinSize={size}
                color={isDisabled ? 'gray-disabled' : undefined}
                role={isDisabled ? 'supplementary' : 'main'}
            >
                {content}
            </TextGenericWithSkin>
        </li>
    );
};

/**
 * Внутренняя реализация ParagraphList
 */
const InternalParagraphList: FC<ParagraphListProps<true>> = function InternalParagraphList({
    className,
    'data-testid': dataTestId = 'ParagraphList',
    size,
    color = 'gray',
    role,
    title,
    isCompact,
    ...props
}) {
    // Костыль DIRECT-238538
    const sizeSkinClass = getSizeSkin(size === 's' ? 'xs' : 'm');
    const { textClasses: textTypographyClasses } = useTypographyWithSkin({
        size: 'current-main',
        color,
        role,
    });

    const { view } = props;
    const ParagraphListTag = view === 'numbered' ? 'ol' : 'ul';
    const level = 'level' in props ? props.level : 1;
    const { addonClasses } = useInternalVerticalAlignWithSkin({
        textSize: 'current-main',
        addonType: 'icon',
    });
    const baseDataTestid = level ? `${dataTestId}.Level${level}` : dataTestId;

    const isIconsDisabled = (props.view === 'yes-no-bullet' && props.isIconsDisabled) || false;

    const listContent = useMemo(() => {
        if (props.view === 'yes-no-bullet') {
            return props.items.map((item, i) => {
                const { isYes, content, isDisabled } = item;
                const dataTestItemId = item['data-testid'] || `${baseDataTestid}.ListItem.${i + 1}`;

                return (
                    <InternalParagraphListYesNoItem
                        isYes={isYes}
                        content={content}
                        key={`list-item-${i}`}
                        size={size}
                        className={addonClasses}
                        data-testid={dataTestItemId}
                        isDisabled={isDisabled || isIconsDisabled}
                    />
                );
            });
        }

        return props.items.map((item, i) => {
            let content: ReactNode;

            if (isInnerParagraphListItem(item)) {
                content = (
                    <InternalParagraphList
                        className={cls('inner-list')}
                        title={item.title}
                        items={item.items}
                        level={level + 1}
                        view={item.view ?? props.view}
                        size={size}
                        color={color}
                        role={role}
                        isCompact={isCompact}
                    />
                );
            } else {
                content = item.content;
            }

            const itemDataTestId = item['data-testid'] || `${baseDataTestid}.ListItem.${i + 1}`;

            return (
                <li className={cls('list-item', { level })} key={`list-item-${i}`} data-testid={itemDataTestId}>
                    <div>{content}</div>
                </li>
            );
        });
    }, [props.view, props.items, baseDataTestid, addonClasses, isIconsDisabled, level, size, color, role, isCompact]);

    const list = (
        <ParagraphListTag
            className={cls('list', { view, isCompact }, [
                sizeSkinClass,
                ...(title ? [] : [textTypographyClasses, className]),
            ])}
            data-testid={`${baseDataTestid}.List`}
        >
            {listContent}
        </ParagraphListTag>
    );

    if (title) {
        return (
            <ParagraphListTitleWrapper
                title={title}
                list={list}
                baseDataTestid={baseDataTestid}
                className={getClassNames([sizeSkinClass, className])}
                textTypographyClasses={textTypographyClasses}
                isCompact={isCompact}
            />
        );
    }

    return list;
};

/**
 * Компонент для отрисовки списка с заданым заголовком
 */
const ParagraphListTitleWrapper = (props: {
    /**
     * Класс типографики, используемый в списке
     */
    textTypographyClasses: string;
    /**
     * Класс переданный в компонент из вне
     */
    className?: string;
    /**
     * Основа data-testid
     */
    baseDataTestid: string;
    /**
     * Заголовок
     */
    title: ReactNode;
    /**
     * ReactNode элементов списка или одного элемента
     */
    list: ReactNode;
    /**
     * Компактный вариант списка без отступов между элементами
     */
    isCompact?: boolean;
}) => {
    const { baseDataTestid, textTypographyClasses, className, title, list, isCompact } = props;

    return (
        <figure className={cls(undefined, [textTypographyClasses, className])} data-testid={baseDataTestid}>
            <figcaption className={cls('title', { isCompact })} data-testid={`${baseDataTestid}.Title`}>
                {title}
            </figcaption>
            {list}
        </figure>
    );
};

/**
 * Компонент для отрисовки маркированного/нумерованного списка
 */
export const ParagraphList = memo<ParagraphListProps>(function ParagraphList({
    header,
    size = 'm',
    color = 'gray',
    isCompact,
    ...props
}) {
    const headerLevel = paragraphListHeaderLevels[size];
    const title = useMemo(
        () => (header ? header({ level: headerLevel, color }) : undefined),
        [color, header, headerLevel],
    );

    const baseInternalListProps = useMemo(
        () => ({
            size,
            title,
            color,
            isCompact,
        }),
        [color, size, title, isCompact],
    );

    const internalListProps = useMemo(() => {
        switch (props.view) {
            case 'bullet':
                return { ...props, ...baseInternalListProps, level: 1 };
            case 'numbered':
                return { ...props, ...baseInternalListProps, level: 1 };
            case 'yes-no-bullet':
                return {
                    ...props,
                    ...baseInternalListProps,
                };
        }
    }, [props, baseInternalListProps]);

    return <InternalParagraphList {...internalListProps} />;
});
